import { useState, useEffect } from "react";
import axios from "axios";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useAuthContext } from "../../hooks/useAuthContext";
import Loading from "../Loading";
import { useTranslation } from "react-i18next";

const Dashboard = () => {
  const { t } = useTranslation();

  const { user } = useAuthContext();
  const [occupationData, setOccupationData] = useState([]);
  const [retentionData, setRetentionData] = useState([]);
  const [topPlayersData, setTopPlayersData] = useState([]);
  const [revenueData, setRevenueData] = useState([]);

  const [loadingOccupation, setLoadingOccupation] = useState(false);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [loadingRevenue, setLoadingRevenue] = useState(false);

  const loading = loadingOccupation || loadingPlayers || loadingRevenue;

  const clubDetailsString = localStorage.getItem("club_details");
  const club_details = JSON.parse(clubDetailsString);

  useEffect(() => {
    getRevenueData();
    getOccupationData();
    getPlayersData();
  }, []);

  const getRevenueData = async () => {
    setLoadingRevenue(true);
    try {
      const response = await axios.get(
        `/api/dashboard/income?clubId=${club_details.club_id}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      const sortedRevenueData = [...response.data].sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      );

      setRevenueData(sortedRevenueData);
    } catch (error) {
      console.error("Error fetching all configs:", error);
    } finally {
      setLoadingRevenue(false);
    }
  };

  const getOccupationData = async () => {
    setLoadingOccupation(true);
    try {
      const response = await axios.get(
        `/api/dashboard/bookings?clubId=${club_details.club_id}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      const sortedOccupationData = [...response.data].sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      );

      setOccupationData(sortedOccupationData);
    } catch (error) {
      console.error("Error fetching all configs:", error);
    } finally {
      setLoadingOccupation(false);
    }
  };

  const getPlayersData = async () => {
    setLoadingPlayers(true);
    try {
      const response = await axios.get(
        `/api/dashboard/players?clubId=${club_details.club_id}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      const formattedRetentionData = response?.data?.monthlyRetention.map(
        ([month, newCount, returnCount, retentionRate]) => ({
          month,
          newCount,
          returnCount,
          retentionRate,
        })
      );

      const formattedTopPlayerData = response?.data?.filteredPlayers.map(
        ([, name, matches]) => ({
          name,
          matches,
        })
      );

      setRetentionData(formattedRetentionData);
      setTopPlayersData(formattedTopPlayerData);
    } catch (error) {
      console.error("Error fetching all configs:", error);
    } finally {
      setLoadingPlayers(false);
    }
  };

  return (
    <div className="w-full px-4">
      <h1 className="pt-6 pb-1 text-xl font-bold px-4 text-primary">
        {t("KpiDashboard")}
      </h1>
      <hr className=" h-[1.5px] bg-primary mb-6" />
      {loading ? (
        <Loading />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6 text-primary">
            {/* Occupation Rate */}
            <div className="p-4 rounded-lg border border-gray-700 bg-[#1a1a1a] text-card-foreground shadow-sm">
              <h3 className="font-bold mb-4">{t("CourtOccupancy")}</h3>
              {occupationData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={occupationData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="day"
                      tickFormatter={(name) =>
                        name.charAt(0).toUpperCase() + name.slice(1)
                      }
                    />
                    <YAxis domain={[0, 100]} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "black",
                        borderColor: "#333",
                      }}
                      labelStyle={{
                        color: "white",
                      }}
                      itemStyle={{
                        color: "#DCF02DFF",
                      }}
                    />
                    <Legend />
                    <Bar
                      dataKey="occupancyRate"
                      name={t("Occupation")}
                      fill="#DCF02D"
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted-foreground text-sm text-center text-white">
                  {t("NoOccupancyData")}
                </p>
              )}
            </div>

            {/* Retention Rate */}
            <div className="p-4 rounded-lg border border-gray-700 bg-[#1a1a1a] text-card-foreground shadow-sm text-primary">
              <h3 className="font-bold mb-4">{t("PlayerRetentionRate")}</h3>
              {retentionData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={[...retentionData].reverse()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis domain={[50, 100]} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "black",
                        borderColor: "#333",
                      }}
                      labelStyle={{
                        color: "white",
                      }}
                      itemStyle={{
                        color: "#DCF02DFF",
                      }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="retentionRate"
                      name={t("Retention")}
                      stroke="#DCF02DFF"
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted-foreground text-sm text-center text-white">
                  {t("NoRetentionData")}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-primary">
            {/* Revenue per Court */}
            <div className="p-4 rounded-lg border border-gray-700 bg-[#1a1a1a] text-card-foreground shadow-sm">
              <h3 className="font-bold mb-4">{t("IncomePerDay")}</h3>
              {revenueData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" />
                    <YAxis />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "black",
                        borderColor: "#333",
                      }}
                      labelStyle={{
                        color: "white",
                      }}
                      itemStyle={{
                        color: "#DCF02DFF",
                      }}
                    />
                    <Legend />
                    <Bar
                      dataKey="dailyOnlineIncome"
                      name={t("Income")}
                      fill="#DCF02D"
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted-foreground text-sm text-center text-white">
                  {t("Noincomedata")}
                </p>
              )}
            </div>

            {/* Top Players */}
            <div className="p-4 rounded-lg border border-gray-700 bg-[#1a1a1a] text-card-foreground shadow-sm">
              <h3 className="font-bold mb-4">
                {t("PlayersWithTheMostMatches")}
              </h3>
              <div className="overflow-auto text-primary">
                {topPlayersData.length > 0 ? (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">{t("Player")}</th>
                        <th className="text-right py-2">{t("Matches")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topPlayersData.map((player, index) => (
                        <tr key={index} className="border-b">
                          <td className="py-2">{player.name}</td>
                          <td className="text-right py-2">{player.matches}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-muted-foreground text-sm text-center text-white">
                    {t("ThereAreNoFeaturedPlayers")}
                  </p>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
